import React, { useState, useEffect } from 'react';
import { getWhatsAppInsights, getWhatsAppTeamOverview, getWhatsAppCampaignReport, getWhatsAppTeamPerformance } from '../utils/api';
import { FaUsers, FaClock, FaHome, FaMapMarkerAlt, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { ar, en } from '../utils/translations';
import DualCalendarPicker from './DualCalendarPicker';
import './WhatsApp.css';

const WhatsAppInsights = ({ language = 'ar', userRole = 'employee', focusedCampaignId = null, onNavigateToInbox, onNavigateToSupervisor, onNavigateToCampaigns, onOpenConversation }) => {
  const t = language === 'ar' ? ar : en;
  const [insights, setInsights] = useState(null);
  const [teamOverview, setTeamOverview] = useState(null);
  const [campaignReport, setCampaignReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeView, setActiveView] = useState('overview'); // overview | campaigns | team
  const [teamPerformance, setTeamPerformance] = useState(null);
  const [perfPeriod, setPerfPeriod] = useState('all'); // today | week | month | custom | all
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfDateFrom, setPerfDateFrom] = useState('');
  const [perfDateTo, setPerfDateTo] = useState('');
  const [perfTimeMode, setPerfTimeMode] = useState('business'); // business | wall_clock
  const [campaignSortBy, setCampaignSortBy] = useState('reply_rate'); // reply_rate | replies | outbound
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const [campaignDateRange, setCampaignDateRange] = useState('all'); // all | 7d | 30d | custom
  const [campaignDateFrom, setCampaignDateFrom] = useState('');
  const [campaignDateTo, setCampaignDateTo] = useState('');
  const [showOverallAnalytics, setShowOverallAnalytics] = useState(false);
  const [mockData] = useState(false); // Use live API data by default
  const isManagerOrOwner = userRole === 'manager' || userRole === 'owner';

  // Mock data for testing
  const mockInsights = {
    clients_waiting: [
      { id: '1', phone: '+966501234567', last_message: 'أبحث عن شقة', last_message_at: new Date(Date.now() - 3600000).toISOString() },
      { id: '2', phone: '+966507654321', last_message: 'ما هو السعر؟', last_message_at: new Date(Date.now() - 7200000).toISOString() }
    ],
    avg_response_time: 45, // minutes
    most_requested_property_type: 'apartment',
    most_mentioned_area: 'العليا'
  };

  useEffect(() => {
    loadInsights();
  }, []);

  useEffect(() => {
    if (activeView !== 'team') return;
    // For custom, only fetch when both dates are filled
    if (perfPeriod === 'custom') {
      if (perfDateFrom && perfDateTo && perfDateFrom <= perfDateTo) {
        loadTeamPerformance('custom', perfDateFrom, perfDateTo, perfTimeMode);
      }
    } else {
      loadTeamPerformance(perfPeriod, '', '', perfTimeMode);
    }
  }, [perfPeriod, perfDateFrom, perfDateTo, activeView, perfTimeMode]);

  useEffect(() => {
    if (focusedCampaignId) {
      setActiveView('campaigns');
      setCampaignSortBy('outbound');
      setShowOverallAnalytics(false);
    }
  }, [focusedCampaignId]);

  const loadTeamPerformance = async (period = perfPeriod, dateFrom = perfDateFrom, dateTo = perfDateTo, timeMode = perfTimeMode) => {
    try {
      setPerfLoading(true);
      const res = await getWhatsAppTeamPerformance(period, dateFrom, dateTo, timeMode);
      setTeamPerformance(res.data || null);
    } catch (err) {
      console.error('Failed to load team performance:', err);
    } finally {
      setPerfLoading(false);
    }
  };

  const loadInsights = async () => {
    try {
      setLoading(true);
      if (mockData) {
        // Use mock data
        setTimeout(() => {
          setInsights(mockInsights);
          setLoading(false);
        }, 500);
      } else {
        const [insightsRes, teamRes, campaignRes, perfRes] = await Promise.allSettled([
          getWhatsAppInsights(),
          getWhatsAppTeamOverview(),
          getWhatsAppCampaignReport(),
          getWhatsAppTeamPerformance()
        ]);
        setInsights(insightsRes.status === 'fulfilled' ? insightsRes.value.data : mockInsights);
        setTeamOverview(teamRes.status === 'fulfilled' ? teamRes.value.data : null);
        setTeamPerformance(perfRes.status === 'fulfilled' ? perfRes.value.data : null);
        const reportRows = campaignRes.status === 'fulfilled' ? (campaignRes.value.data?.campaigns || []) : [];

        const campaigns = reportRows.map((rep) => ({
              campaign_id: rep.campaign_id,
              campaign_name: rep.campaign_name,
              template_name: rep.template_name,
              status: rep.status,
              created_at: rep.created_at || null,
              started_at: rep.started_at || null,
              completed_at: rep.completed_at || null,
              total_recipients: Number(rep.total_recipients || 0),
              outbound_count: Number(rep.outbound_count || 0),
              delivered_count: Number(rep.delivered_count || 0),
              read_count: Number(rep.read_count || 0),
              failed_count: Number(rep.failed_count || 0),
              reply_count: Number(rep.reply_count || 0),
              attributed_conversations: Number(rep.attributed_conversations || 0)
        }));

        setCampaignReport(campaigns);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading insights:', error);
      // Fallback to mock data on error
      setInsights(mockInsights);
      setLoading(false);
    }
  };
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadInsights();
    } finally {
      setRefreshing(false);
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return language === 'ar' ? 'لا توجد بيانات' : 'No data';
    if (minutes < 60) return `${minutes} ${language === 'ar' ? 'دقيقة' : 'min'}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours} ${language === 'ar' ? 'ساعة' : 'h'} ${mins > 0 ? `${mins} ${language === 'ar' ? 'د' : 'm'}` : ''}`;
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      'apartment': language === 'ar' ? 'شقة' : 'Apartment',
      'villa': language === 'ar' ? 'فيلا' : 'Villa',
      'land': language === 'ar' ? 'أرض' : 'Land',
      'unknown': language === 'ar' ? 'غير محدد' : 'Unknown'
    };
    return labels[type] || labels['unknown'];
  };
  const getResponseHealthLabel = (minutes) => {
    if (!minutes || Number.isNaN(Number(minutes))) {
      return language === 'ar' ? 'غير متوفر' : 'Unavailable';
    }
    if (minutes <= 15) return language === 'ar' ? 'ممتاز' : 'Excellent';
    if (minutes <= 30) return language === 'ar' ? 'جيد' : 'Good';
    if (minutes <= 60) return language === 'ar' ? 'متوسط' : 'Fair';
    return language === 'ar' ? 'يحتاج تحسين' : 'Needs Improvement';
  };
  const getResponseHealthColor = (minutes) => {
    if (!minutes || Number.isNaN(Number(minutes))) return '#6b7280';
    if (minutes <= 15) return '#059669';
    if (minutes <= 30) return '#0ea5e9';
    if (minutes <= 60) return '#d97706';
    return '#dc2626';
  };
  const getCampaignLabel = (campaign) => {
    const id = campaign?.campaign_id;
    const name = campaign?.campaign_name;
    if (name && String(name).trim()) return String(name).trim();
    return `#${id ?? '-'}`;
  };
  const getCampaignTimestamp = (campaign) => {
    const ts = campaign?.completed_at || campaign?.started_at || campaign?.created_at;
    const ms = ts ? new Date(ts).getTime() : NaN;
    return Number.isFinite(ms) ? ms : null;
  };
  const nowMs = Date.now();
  const dateFilteredCampaignReport = campaignReport.filter((campaign) => {
    if (campaignDateRange === 'all') return true;
    const ts = getCampaignTimestamp(campaign);
    if (!ts) return true;
    if (campaignDateRange === 'custom') {
      const fromMs = campaignDateFrom ? new Date(`${campaignDateFrom}T00:00:00`).getTime() : null;
      const toMs = campaignDateTo ? new Date(`${campaignDateTo}T23:59:59`).getTime() : null;
      if (fromMs && ts < fromMs) return false;
      if (toMs && ts > toMs) return false;
      return true;
    }
    const cutoff = campaignDateRange === '7d'
      ? nowMs - (7 * 24 * 60 * 60 * 1000)
      : nowMs - (30 * 24 * 60 * 60 * 1000);
    return ts >= cutoff;
  });
  const searchedCampaignReport = dateFilteredCampaignReport.filter((campaign) => {
    const q = String(campaignSearch || '').trim().toLowerCase();
    if (!q) return true;
    const label = String(getCampaignLabel(campaign) || '').toLowerCase();
    const template = String(campaign.template_name || '').toLowerCase();
    return label.includes(q) || template.includes(q);
  });
  const filteredCampaignReport = searchedCampaignReport.filter((campaign) => {
    if (campaignStatusFilter === 'all') return true;
    const status = String(campaign.status || '').toLowerCase();
    if (campaignStatusFilter === 'running') return status === 'running' || status === 'sending';
    return status === campaignStatusFilter;
  });
  const MIN_OUTBOUND_FOR_RATE_RANKING = 5;
  const sortedCampaignReport = [...filteredCampaignReport].sort((a, b) => {
    const aOutbound = Number(a.outbound_count || 0);
    const bOutbound = Number(b.outbound_count || 0);
    const aReplies = Number(a.reply_count || 0);
    const bReplies = Number(b.reply_count || 0);
    const aRate = aOutbound >= MIN_OUTBOUND_FOR_RATE_RANKING ? aReplies / aOutbound : 0;
    const bRate = bOutbound >= MIN_OUTBOUND_FOR_RATE_RANKING ? bReplies / bOutbound : 0;
    if (campaignSortBy === 'replies') return bReplies - aReplies;
    if (campaignSortBy === 'outbound') return bOutbound - aOutbound;
    // Default: rate ranking — campaigns below minimum threshold are pushed to the bottom
    if (aRate === bRate) return bOutbound - aOutbound;
    return bRate - aRate;
  });
  const focusedCampaign = focusedCampaignId ? campaignReport.find((c) => String(c.campaign_id) === String(focusedCampaignId)) : null;
  const campaignScope = focusedCampaign ? [focusedCampaign] : sortedCampaignReport;
  const campaignTotals = campaignScope.reduce((acc, item) => {
    acc.target += Number(item.total_recipients || 0);
    acc.outbound += Number(item.outbound_count || 0);
    acc.delivered += Number(item.delivered_count || 0);
    acc.read += Number(item.read_count || 0);
    acc.failed += Number(item.failed_count || 0);
    acc.replies += Number(item.reply_count || 0);
    acc.attributed += Number(item.attributed_conversations || 0);
    return acc;
  }, { target: 0, outbound: 0, delivered: 0, read: 0, failed: 0, replies: 0, attributed: 0 });
  const deliveryRate = campaignTotals.outbound > 0 ? Math.round((campaignTotals.delivered / campaignTotals.outbound) * 100) : 0;
  const readRate = campaignTotals.delivered > 0 ? Math.round((campaignTotals.read / campaignTotals.delivered) * 100) : 0;
  const replyRate = campaignTotals.outbound > 0 ? Math.round((campaignTotals.replies / campaignTotals.outbound) * 100) : 0;
  const notDeliveredCount = Math.max(0, campaignTotals.outbound - campaignTotals.delivered);
  const unreadCount = Math.max(0, campaignTotals.delivered - campaignTotals.read);
  const noReplyAfterReadCount = Math.max(0, campaignTotals.read - campaignTotals.replies);
  const notDeliveredRate = campaignTotals.outbound > 0 ? Math.round((notDeliveredCount / campaignTotals.outbound) * 100) : 0;
  const unreadRate = campaignTotals.delivered > 0 ? Math.round((unreadCount / campaignTotals.delivered) * 100) : 0;
  const noReplyAfterReadRate = campaignTotals.read > 0 ? Math.round((noReplyAfterReadCount / campaignTotals.read) * 100) : 0;
  const campaignDiagnostics = sortedCampaignReport
    .map((campaign) => {
      const outbound = Number(campaign.outbound_count || 0);
      const delivered = Number(campaign.delivered_count || 0);
      const read = Number(campaign.read_count || 0);
      const replies = Number(campaign.reply_count || 0);
      const notDelivered = Math.max(0, outbound - delivered);
      const unread = Math.max(0, delivered - read);
      const noReplyAfterRead = Math.max(0, read - replies);
      return {
        id: campaign.campaign_id,
        outbound,
        notDelivered,
        unread,
        noReplyAfterRead,
        notDeliveredRate: outbound > 0 ? Math.round((notDelivered / outbound) * 100) : 0,
        unreadRate: delivered > 0 ? Math.round((unread / delivered) * 100) : 0,
        noReplyAfterReadRate: read > 0 ? Math.round((noReplyAfterRead / read) * 100) : 0
      };
    })
    .filter((item) => item.outbound > 0);
  const topDeliveryRisk = [...campaignDiagnostics]
    .sort((a, b) => (b.notDeliveredRate - a.notDeliveredRate) || (b.notDelivered - a.notDelivered))[0];
  const topReadRisk = [...campaignDiagnostics]
    .filter((item) => item.outbound > 0 && item.unread > 0)
    .sort((a, b) => (b.unreadRate - a.unreadRate) || (b.unread - a.unread))[0];
  const topEngagementRisk = [...campaignDiagnostics]
    .filter((item) => item.outbound > 0 && item.noReplyAfterRead > 0)
    .sort((a, b) => (b.noReplyAfterReadRate - a.noReplyAfterReadRate) || (b.noReplyAfterRead - a.noReplyAfterRead))[0];
  const shouldShowManagerDeepPanels = isManagerOrOwner && (!focusedCampaign || showOverallAnalytics);
  const isFocusedCampaignMode = Boolean(focusedCampaign && !showOverallAnalytics);
  const slaWarningMinutes = Number(teamOverview?.sla_warning_minutes || 10);
  const slaCriticalMinutes = Number(teamOverview?.sla_critical_minutes || 60);
  const hasActiveCampaignFilters =
    campaignSortBy !== 'reply_rate' ||
    campaignStatusFilter !== 'all' ||
    campaignDateRange !== 'all' ||
    Boolean(campaignDateFrom) ||
    Boolean(campaignDateTo) ||
    Boolean(String(campaignSearch || '').trim());

  if (loading) {
    return (
      <div className="wa-loading">
        <div className="wa-loading-spinner" />
        <p className="wa-loading-text">
          {language === 'ar' ? 'جاري تحميل البيانات...' : 'Loading insights...'}
        </p>
      </div>
    );
  }

  return (
    <div className="wa-animate-in wa-insights-root">
      <div className="wa-insights-header">
        <h1 className="wa-insights-title">
          {language === 'ar' ? 'تحليلات الواتساب' : 'WhatsApp Insights'}
        </h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setActiveView('overview')}
              className={`wa-view-pill ${activeView === 'overview' ? 'active' : ''}`}
            >
              {language === 'ar' ? 'نظرة عامة' : 'Overview'}
            </button>
            <button
              onClick={() => setActiveView('campaigns')}
              className={`wa-view-pill ${activeView === 'campaigns' ? 'active' : ''}`}
            >
              {language === 'ar' ? 'الحملات' : 'Campaigns'}
            </button>
            {isManagerOrOwner && (
              <button
                onClick={() => setActiveView('team')}
                className={`wa-view-pill ${activeView === 'team' ? 'active' : ''}`}
              >
                {language === 'ar' ? 'أداء الفريق' : 'Team'}
              </button>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="wa-btn wa-btn-subtle"
          >
            {refreshing
              ? (language === 'ar' ? 'جاري التحديث...' : 'Refreshing...')
              : (language === 'ar' ? 'تحديث' : 'Refresh')}
          </button>
        </div>
      </div>
      <div
        className="wa-health-badge"
        style={{
          marginBottom: '16px',
          backgroundColor: `${getResponseHealthColor(insights?.avg_response_time)}15`,
          borderColor: `${getResponseHealthColor(insights?.avg_response_time)}40`
        }}
      >
        <span style={{ color: 'var(--wa-text-muted)' }}>
          {language === 'ar' ? 'حالة سرعة الرد' : 'Response speed health'}:
        </span>
        <span style={{ color: getResponseHealthColor(insights?.avg_response_time) }}>
          {getResponseHealthLabel(insights?.avg_response_time)}
        </span>
      </div>

      {activeView === 'overview' && (
      <div className="wa-insights-grid">

        {/* ── Card 1: Clients Waiting ── */}
        <div
          role={(onNavigateToSupervisor || onNavigateToInbox) ? 'button' : undefined}
          tabIndex={(onNavigateToSupervisor || onNavigateToInbox) ? 0 : undefined}
          onClick={(onNavigateToSupervisor || onNavigateToInbox) && (insights?.clients_waiting?.length > 0) ? (onNavigateToSupervisor || onNavigateToInbox) : undefined}
          onKeyDown={(onNavigateToSupervisor || onNavigateToInbox) && (insights?.clients_waiting?.length > 0) ? (e) => { if (e.key === 'Enter') (onNavigateToSupervisor || onNavigateToInbox)(); } : undefined}
          className="wa-metric-card"
          style={{ '--card-accent': '#f59e0b', cursor: ((onNavigateToSupervisor || onNavigateToInbox) && insights?.clients_waiting?.length > 0) ? 'pointer' : 'default' }}
        >
          {(onNavigateToSupervisor || onNavigateToInbox) && <span className="wa-metric-nav-hint">↗</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div className="wa-metric-icon" style={{ backgroundColor: '#fef3c7' }}>
              <FaUsers style={{ color: '#f59e0b' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="wa-metric-label">{language === 'ar' ? 'في انتظار الرد' : 'Waiting for Reply'}</div>
              <div className="wa-metric-value">{insights?.clients_waiting?.length || 0}</div>
            </div>
          </div>
          {insights?.clients_waiting && insights.clients_waiting.length > 0 && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(229,231,235,0.7)' }}>
              {insights.clients_waiting.slice(0, 2).map((client) => (
                <div
                  key={client.id}
                  onClick={onOpenConversation ? (e) => { e.stopPropagation(); onOpenConversation(client.id); } : undefined}
                  role={onOpenConversation ? 'button' : undefined}
                  tabIndex={onOpenConversation ? 0 : undefined}
                  style={{ padding: '4px 0', fontSize: '12px', color: '#4b5563', cursor: onOpenConversation ? 'pointer' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {client.phone}
                </div>
              ))}
              {insights.clients_waiting.length > 2 && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                  +{insights.clients_waiting.length - 2} {language === 'ar' ? 'أخرى' : 'more'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Card 2: Avg Response Time ── */}
        <div
          className="wa-metric-card"
          role={onNavigateToInbox ? 'button' : undefined}
          tabIndex={onNavigateToInbox ? 0 : undefined}
          onClick={onNavigateToInbox || undefined}
          onKeyDown={onNavigateToInbox ? (e) => { if (e.key === 'Enter') onNavigateToInbox(); } : undefined}
          style={{ '--card-accent': '#3b82f6', cursor: onNavigateToInbox ? 'pointer' : 'default' }}
        >
          {onNavigateToInbox && <span className="wa-metric-nav-hint">↗</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div className="wa-metric-icon" style={{ backgroundColor: '#dbeafe' }}>
              <FaClock style={{ color: '#3b82f6' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="wa-metric-label">{language === 'ar' ? 'متوسط وقت الرد' : 'Avg Response Time'}</div>
              <div className="wa-metric-value">{formatTime(insights?.avg_response_time)}</div>
              </div>
              </div>
          <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(229,231,235,0.7)', fontSize: '11px', color: '#94a3b8' }}>
            {language === 'ar' ? 'متوسط الرد على رسائل العملاء' : 'Average reply time to client messages'}
            </div>
        </div>

        {/* ── Card 3: Most Requested Property Type ── */}
        <div
          className="wa-metric-card"
          role={onNavigateToInbox ? 'button' : undefined}
          tabIndex={onNavigateToInbox ? 0 : undefined}
          onClick={onNavigateToInbox || undefined}
          onKeyDown={onNavigateToInbox ? (e) => { if (e.key === 'Enter') onNavigateToInbox(); } : undefined}
          style={{ '--card-accent': '#ec4899', cursor: onNavigateToInbox ? 'pointer' : 'default' }}
        >
          {onNavigateToInbox && <span className="wa-metric-nav-hint">↗</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div className="wa-metric-icon" style={{ backgroundColor: '#fce7f3' }}>
              <FaHome style={{ color: '#ec4899' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="wa-metric-label">{language === 'ar' ? 'النوع الأكثر طلباً' : 'Most Requested Type'}</div>
              <div className="wa-metric-value">{getPropertyTypeLabel(insights?.most_requested_property_type)}</div>
              </div>
              </div>
          <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(229,231,235,0.7)', fontSize: '11px', color: '#94a3b8' }}>
            {language === 'ar' ? 'النوع الأكثر طلباً في المحادثات' : 'Most requested type in conversations'}
          </div>
        </div>

        {/* ── Card 4: Most Mentioned Area ── */}
        <div
          className="wa-metric-card"
          role={onNavigateToInbox ? 'button' : undefined}
          tabIndex={onNavigateToInbox ? 0 : undefined}
          onClick={onNavigateToInbox || undefined}
          onKeyDown={onNavigateToInbox ? (e) => { if (e.key === 'Enter') onNavigateToInbox(); } : undefined}
          style={{ '--card-accent': '#10b981', cursor: onNavigateToInbox ? 'pointer' : 'default' }}
        >
          {onNavigateToInbox && <span className="wa-metric-nav-hint">↗</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div className="wa-metric-icon" style={{ backgroundColor: '#dcfce7' }}>
              <FaMapMarkerAlt style={{ color: '#10b981' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="wa-metric-label">{language === 'ar' ? 'المنطقة الأكثر ذكراً' : 'Most Mentioned Area'}</div>
              <div className="wa-metric-value" style={{ fontSize: '20px' }}>
                {insights?.most_mentioned_area || (language === 'ar' ? 'لا توجد بيانات' : 'No data')}
              </div>
            </div>
          </div>
          <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(229,231,235,0.7)', fontSize: '11px', color: '#94a3b8' }}>
            {language === 'ar' ? 'الأكثر ذكراً في المحادثات' : 'Most mentioned area in conversations'}
          </div>
        </div>

        {/* ── Card 5: Top 3 Agents — managers/owners only ── */}
        {isManagerOrOwner && (() => {
          const top3 = (teamPerformance?.agents || []).filter((a) => Number(a.messages_sent) > 0).slice(0, 3);
          const medals = ['🥇', '🥈', '🥉'];
          return (
            <div
              role="button" tabIndex={0}
              onClick={() => setActiveView('team')}
              onKeyDown={(e) => { if (e.key === 'Enter') setActiveView('team'); }}
              className="wa-metric-card"
              style={{ '--card-accent': '#f97316', cursor: 'pointer' }}
            >
              <span className="wa-metric-nav-hint">↗</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div className="wa-metric-icon" style={{ backgroundColor: '#fff7ed', fontSize: '18px' }}>🏆</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="wa-metric-label">{language === 'ar' ? 'أفضل 3 موظفين' : 'Top 3 Agents'}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>
                    {language === 'ar' ? 'كل الوقت' : 'All time'}
            </div>
              </div>
              </div>
              <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(229,231,235,0.7)' }}>
                {top3.length === 0 ? (
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{language === 'ar' ? 'لا توجد بيانات بعد' : 'No data yet'}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {top3.map((agent, idx) => (
                      <div key={agent.user_id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px', width: '18px', flexShrink: 0 }}>{medals[idx]}</span>
                        <div style={{ flex: 1, minWidth: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {agent.agent_name}
            </div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#ea580c', background: '#fff7ed', borderRadius: '6px', padding: '1px 5px', flexShrink: 0 }}>
                          {Number(agent.messages_sent).toLocaleString()}
          </div>
                </div>
              ))}
            </div>
          )}
            </div>
        </div>
          );
        })()}

        {/* ── Card 6: SLA ── */}
        <div
          role={(onNavigateToSupervisor && (teamOverview?.breached_warning > 0 || teamOverview?.breached_critical > 0)) ? 'button' : undefined}
          tabIndex={(onNavigateToSupervisor && (teamOverview?.breached_warning > 0 || teamOverview?.breached_critical > 0)) ? 0 : undefined}
          onClick={onNavigateToSupervisor && (teamOverview?.breached_warning > 0 || teamOverview?.breached_critical > 0) ? onNavigateToSupervisor : undefined}
          onKeyDown={(onNavigateToSupervisor && (teamOverview?.breached_warning > 0 || teamOverview?.breached_critical > 0)) ? (e) => { if (e.key === 'Enter') onNavigateToSupervisor(); } : undefined}
          className="wa-metric-card"
          style={{ '--card-accent': '#d97706', cursor: (onNavigateToSupervisor && (teamOverview?.breached_warning > 0 || teamOverview?.breached_critical > 0)) ? 'pointer' : 'default' }}
        >
          {(onNavigateToSupervisor && (teamOverview?.breached_warning > 0 || teamOverview?.breached_critical > 0)) && <span className="wa-metric-nav-hint">↗</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div className="wa-metric-icon" style={{ backgroundColor: '#fef3c7' }}>
              <FaExclamationTriangle style={{ color: '#d97706' }} />
          </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="wa-metric-label">{language === 'ar' ? 'مستوى الخدمة (SLA)' : 'Service Level (SLA)'}</div>
              <div className="wa-metric-value">{(teamOverview?.breached_warning || 0) + (teamOverview?.breached_critical || 0)}</div>
              </div>
              </div>
          <div style={{ paddingTop: '8px', borderTop: '1px solid rgba(229,231,235,0.7)', display: 'flex', gap: '14px' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#b45309' }}>{teamOverview?.breached_warning || 0}</div>
              <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                {language === 'ar' ? `تحذير (${slaWarningMinutes}+د)` : `Warn (${slaWarningMinutes}+m)`}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#b91c1c' }}>{teamOverview?.breached_critical || 0}</div>
              <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                {language === 'ar' ? `حرج (${slaCriticalMinutes}+د)` : `Crit (${slaCriticalMinutes}+m)`}
                    </div>
                    </div>
                  </div>
            </div>

      </div>
      )}
      {activeView === 'campaigns' && (
        <div style={{ display: 'grid', gap: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>
              {isFocusedCampaignMode
                ? (language === 'ar' ? 'أداء الحملة المحددة' : 'Focused Campaign Performance')
                : (language === 'ar' ? 'أداء الحملات' : 'Campaign Performance')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {!isFocusedCampaignMode && (
                <select
                  value={campaignSortBy}
                  onChange={(e) => setCampaignSortBy(e.target.value)}
                  style={{ height: '34px', borderRadius: '8px', border: '1px solid #d5dde8', padding: '0 10px', fontSize: '12px', fontWeight: 600 }}
                >
                  <option value="reply_rate">{language === 'ar' ? 'ترتيب حسب معدل الرد' : 'Sort by reply rate'}</option>
                  <option value="replies">{language === 'ar' ? 'ترتيب حسب الردود' : 'Sort by replies'}</option>
                  <option value="outbound">{language === 'ar' ? 'ترتيب حسب الصادر' : 'Sort by outbound'}</option>
                </select>
              )}
              {!isFocusedCampaignMode && (
                <select
                  value={campaignDateRange}
                  onChange={(e) => setCampaignDateRange(e.target.value)}
                  style={{ height: '34px', borderRadius: '8px', border: '1px solid #d5dde8', padding: '0 10px', fontSize: '12px', fontWeight: 600 }}
                >
                  <option value="all">{language === 'ar' ? 'كل الفترات' : 'All time'}</option>
                  <option value="7d">{language === 'ar' ? 'آخر 7 أيام' : 'Last 7 days'}</option>
                  <option value="30d">{language === 'ar' ? 'آخر 30 يوم' : 'Last 30 days'}</option>
                  <option value="custom">{language === 'ar' ? 'فترة مخصصة' : 'Custom range'}</option>
                </select>
              )}
              {!isFocusedCampaignMode && campaignDateRange === 'custom' && (
                <>
                  <DualCalendarPicker
                    value={campaignDateFrom}
                    onChange={(v) => setCampaignDateFrom(v)}
                    max={campaignDateTo || undefined}
                    language={language}
                    placeholder={language === 'ar' ? 'تاريخ البداية' : 'Start date'}
                    style={{ height: '34px', fontSize: '12px' }}
                  />
                  <DualCalendarPicker
                    value={campaignDateTo}
                    onChange={(v) => setCampaignDateTo(v)}
                    min={campaignDateFrom || undefined}
                    language={language}
                    placeholder={language === 'ar' ? 'تاريخ النهاية' : 'End date'}
                    style={{ height: '34px', fontSize: '12px' }}
                  />
                </>
              )}
              {!isFocusedCampaignMode && (
                <select
                  value={campaignStatusFilter}
                  onChange={(e) => setCampaignStatusFilter(e.target.value)}
                  style={{ height: '34px', borderRadius: '8px', border: '1px solid #d5dde8', padding: '0 10px', fontSize: '12px', fontWeight: 600 }}
                >
                  <option value="all">{language === 'ar' ? 'كل الحالات' : 'All statuses'}</option>
                  <option value="running">{language === 'ar' ? 'جارية' : 'Running'}</option>
                  <option value="completed">{language === 'ar' ? 'مكتملة' : 'Completed'}</option>
                  <option value="scheduled">{language === 'ar' ? 'مجدولة' : 'Scheduled'}</option>
                  <option value="failed">{language === 'ar' ? 'فاشلة' : 'Failed'}</option>
                  <option value="cancelled">{language === 'ar' ? 'ملغاة' : 'Cancelled'}</option>
                </select>
              )}
              {!isFocusedCampaignMode && (
                <input
                  type="text"
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  placeholder={language === 'ar' ? 'بحث باسم الحملة أو القالب' : 'Search campaign or template'}
                  style={{ height: '34px', minWidth: '220px', borderRadius: '8px', border: '1px solid #d5dde8', padding: '0 10px', fontSize: '12px' }}
                />
              )}
              {!isFocusedCampaignMode && hasActiveCampaignFilters && (
                <button
                  type="button"
                  className="wa-btn wa-btn-subtle"
                  onClick={() => {
                    setCampaignSearch('');
                    setCampaignStatusFilter('all');
                    setCampaignDateRange('all');
                    setCampaignDateFrom('');
                    setCampaignDateTo('');
                    setCampaignSortBy('reply_rate');
                  }}
                  style={{ height: '34px', padding: '0 12px', fontSize: '12px' }}
                >
                  {language === 'ar' ? 'إعادة تعيين الفلاتر' : 'Reset filters'}
                </button>
              )}
              {onNavigateToCampaigns && (
                <button
                  className="wa-btn wa-btn-subtle"
                  onClick={onNavigateToCampaigns}
                  style={{ height: '34px', padding: '0 12px', fontSize: '12px' }}
                >
                  {language === 'ar' ? 'فتح الحملات' : 'Open campaigns'}
                </button>
              )}
            </div>
          </div>

          {focusedCampaign && (
            <div style={{ background: 'linear-gradient(135deg,#fff7ed 0%,#fff 100%)', border: '1px solid #fdba74', borderRadius: '12px', padding: '12px 14px', fontSize: '12px', color: '#9a3412' }}>
              {(language === 'ar' ? 'الحملة المحددة من صفحة الحملات' : 'Focused campaign from Campaigns page')}: <strong>{getCampaignLabel(focusedCampaign)}</strong>
              <span style={{ marginInlineStart: '8px', padding: '2px 8px', borderRadius: '9999px', background: '#fff7ed', border: '1px solid #fdba74', fontWeight: 700 }}>
                {language === 'ar' ? 'وضع مركّز' : 'Focused mode'}
              </span>
            </div>
          )}
          {focusedCampaignId && !focusedCampaign && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '12px 14px', fontSize: '12px', color: '#92400e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span>
                {language === 'ar'
                  ? `الحملة ${focusedCampaignId} لا تحتوي بيانات تقرير بعد.`
                  : `Campaign ${focusedCampaignId} has no report data yet.`}
              </span>
              {onNavigateToCampaigns && (
                <button
                  className="wa-btn wa-btn-subtle"
                  onClick={onNavigateToCampaigns}
                  style={{ height: '30px', padding: '0 10px', fontSize: '11px' }}
                >
                  {language === 'ar' ? 'العودة للحملات' : 'Back to campaigns'}
                </button>
              )}
            </div>
          )}
          {focusedCampaign && isManagerOrOwner && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="wa-btn wa-btn-subtle"
                onClick={() => setShowOverallAnalytics((prev) => !prev)}
                style={{ height: '30px', padding: '0 10px', fontSize: '11px' }}
              >
                {showOverallAnalytics
                  ? (language === 'ar' ? 'إخفاء التحليلات العامة' : 'Hide overall analytics')
                  : (language === 'ar' ? 'إظهار التحليلات العامة' : 'Show overall analytics')}
              </button>
            </div>
          )}

          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                {focusedCampaign
                  ? (language === 'ar' ? 'قمع أداء الحملة المحددة' : 'Focused Campaign Funnel')
                  : (language === 'ar' ? 'قمع أداء الحملات' : 'Campaign Funnel')}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>
                {language === 'ar' ? 'المستهدفين ← وصل ← قُرئ ← فشل ← ردود' : 'Target → Delivered → Read → Failed → Replies'}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: '8px' }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#64748b' }}>{language === 'ar' ? 'المستهدفين' : 'Target'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>{campaignTotals.target || campaignTotals.outbound}</div>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#166534' }}>{language === 'ar' ? 'وصل' : 'Delivered'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#15803d' }}>{campaignTotals.delivered}</div>
                <div style={{ fontSize: '10px', color: '#166534' }}>{deliveryRate}%</div>
              </div>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#1e40af' }}>{language === 'ar' ? 'قُرئ' : 'Read'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#2563eb' }}>{campaignTotals.read}</div>
                <div style={{ fontSize: '10px', color: '#1e40af' }}>{readRate}%</div>
              </div>
              <div style={{ background: campaignTotals.failed > 0 ? '#fef2f2' : '#f8fafc', border: `1px solid ${campaignTotals.failed > 0 ? '#fecaca' : '#e2e8f0'}`, borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: campaignTotals.failed > 0 ? '#991b1b' : '#64748b' }}>{language === 'ar' ? 'فشل' : 'Failed'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: campaignTotals.failed > 0 ? '#dc2626' : '#94a3b8' }}>{campaignTotals.failed}</div>
                <div style={{ fontSize: '10px', color: campaignTotals.failed > 0 ? '#991b1b' : '#94a3b8' }}>
                  {campaignTotals.outbound > 0 ? `${Math.round((campaignTotals.failed / campaignTotals.outbound) * 100)}%` : '0%'}
                </div>
              </div>
              <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '10px', padding: '10px' }}>
                <div style={{ fontSize: '11px', color: '#9a3412' }}>{language === 'ar' ? 'ردود' : 'Replies'}</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#c2410c' }}>{campaignTotals.replies}</div>
                <div style={{ fontSize: '10px', color: '#9a3412' }}>{replyRate}%</div>
              </div>
            </div>
            {isManagerOrOwner && (
              <div style={{ marginTop: '10px', fontSize: '11px', color: '#64748b', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <span>{language === 'ar' ? 'محادثات منسوبة' : 'Attributed conversations'}: <strong style={{ color: '#0f172a' }}>{campaignTotals.attributed}</strong></span>
                <span>{language === 'ar' ? 'نسبة القراءة من التسليم' : 'Read from delivered'}: <strong style={{ color: '#0f172a' }}>{readRate}%</strong></span>
                <span>{language === 'ar' ? 'نسبة الرد' : 'Reply rate'}: <strong style={{ color: '#0f172a' }}>{replyRate}%</strong></span>
              </div>
            )}
          </div>
          {shouldShowManagerDeepPanels && (
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
                {language === 'ar' ? 'تشخيص نقاط التسرّب' : 'Drop-off Diagnostics'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '8px' }}>
                <div style={{ borderRadius: '10px', padding: '10px', border: '1px solid #fecaca', background: '#fef2f2' }}>
                  <div style={{ fontSize: '11px', color: '#991b1b' }}>{language === 'ar' ? 'غير مُسلّم بعد' : 'Not delivered yet'}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#dc2626' }}>{notDeliveredCount}</div>
                  <div style={{ fontSize: '10px', color: '#991b1b' }}>{notDeliveredRate}%</div>
                </div>
                <div style={{ borderRadius: '10px', padding: '10px', border: '1px solid #fde68a', background: '#fffbeb' }}>
                  <div style={{ fontSize: '11px', color: '#92400e' }}>{language === 'ar' ? 'مُسلّم غير مقروء' : 'Delivered not read'}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#b45309' }}>{unreadCount}</div>
                  <div style={{ fontSize: '10px', color: '#92400e' }}>{unreadRate}%</div>
                </div>
                <div style={{ borderRadius: '10px', padding: '10px', border: '1px solid #fde68a', background: '#fffbeb' }}>
                  <div style={{ fontSize: '11px', color: '#92400e' }}>{language === 'ar' ? 'مقروء بدون رد' : 'Read with no reply'}</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#b45309' }}>{noReplyAfterReadCount}</div>
                  <div style={{ fontSize: '10px', color: '#92400e' }}>{noReplyAfterReadRate}%</div>
                </div>
              </div>
            </div>
          )}
          {shouldShowManagerDeepPanels && (
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
                {language === 'ar' ? 'ذكاء الإخفاقات (أعلى حملات تحتاج تدخل)' : 'Failure Intelligence (Top campaigns needing action)'}
              </div>
              {campaignDiagnostics.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {language === 'ar' ? 'لا توجد بيانات كافية حتى الآن.' : 'Not enough data yet.'}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '8px' }}>
                  <div style={{ borderRadius: '10px', padding: '10px', border: '1px solid #fecaca', background: '#fef2f2' }}>
                    <div style={{ fontSize: '11px', color: '#991b1b' }}>{language === 'ar' ? 'مخاطر التسليم' : 'Delivery risk'}</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#7f1d1d', fontWeight: 700 }}>#{topDeliveryRisk?.id || '-'}</div>
                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#991b1b' }}>
                      {topDeliveryRisk ? `${topDeliveryRisk.notDelivered} • ${topDeliveryRisk.notDeliveredRate}%` : '-'}
                    </div>
                  </div>
                  <div style={{ borderRadius: '10px', padding: '10px', border: '1px solid #fde68a', background: '#fffbeb' }}>
                    <div style={{ fontSize: '11px', color: '#92400e' }}>{language === 'ar' ? 'مخاطر القراءة' : 'Read risk'}</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#78350f', fontWeight: 700 }}>#{topReadRisk?.id || '-'}</div>
                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#92400e' }}>
                      {topReadRisk ? `${topReadRisk.unread} • ${topReadRisk.unreadRate}%` : '-'}
                    </div>
                  </div>
                  <div style={{ borderRadius: '10px', padding: '10px', border: '1px solid #fde68a', background: '#fffbeb' }}>
                    <div style={{ fontSize: '11px', color: '#92400e' }}>{language === 'ar' ? 'مخاطر التفاعل' : 'Engagement risk'}</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#78350f', fontWeight: 700 }}>#{topEngagementRisk?.id || '-'}</div>
                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#92400e' }}>
                      {topEngagementRisk ? `${topEngagementRisk.noReplyAfterRead} • ${topEngagementRisk.noReplyAfterReadRate}%` : '-'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {sortedCampaignReport.length === 0 ? (
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px', fontSize: '13px', color: '#6b7280' }}>
              {campaignReport.length > 0 && !focusedCampaign
                ? (language === 'ar' ? 'لا توجد حملات مطابقة للفلاتر الحالية' : 'No campaigns match current filters')
                : (language === 'ar' ? 'لا توجد بيانات حملات حتى الآن' : 'No campaign data yet')}
            </div>
          ) : (
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '8px 14px' }}>
              {(focusedCampaign ? [focusedCampaign] : sortedCampaignReport.slice(0, 12)).map((campaign) => {
                const outbound = Number(campaign.outbound_count || 0);
                const delivered = Number(campaign.delivered_count || 0);
                const read = Number(campaign.read_count || 0);
                const failed = Number(campaign.failed_count || 0);
                const replies = Number(campaign.reply_count || 0);
                const deliveryPct = outbound > 0 ? Math.round((delivered / outbound) * 100) : 0;
                const readPct = delivered > 0 ? Math.round((read / delivered) * 100) : 0;
                const replyPct = outbound > 0 ? Math.round((replies / outbound) * 100) : 0;
                const highlighted = focusedCampaignId && String(focusedCampaignId) === String(campaign.campaign_id);
                const riskFlags = [];
                if (outbound >= 20 && deliveryPct < 80) {
                  riskFlags.push(language === 'ar' ? 'تسليم منخفض' : 'Low delivery');
                }
                if (delivered >= 20 && readPct < 35) {
                  riskFlags.push(language === 'ar' ? 'قراءة منخفضة' : 'Low read');
                }
                if (outbound >= 20 && replyPct < 5) {
                  riskFlags.push(language === 'ar' ? 'ردود منخفضة' : 'Low reply');
                }
                return (
                  <div key={campaign.campaign_id} style={{ borderTop: '1px solid #f3f4f6', padding: '12px 0', background: highlighted ? '#fff7ed' : 'transparent', borderRadius: highlighted ? '8px' : 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '13px', color: '#111827', fontWeight: 700 }}>
                      <span>{getCampaignLabel(campaign)}</span>
                      <span style={{ color: '#15803d' }}>{deliveryPct}%</span>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '12px', color: '#64748b', flexWrap: 'wrap' }}>
                      <span>{language === 'ar' ? 'صادر' : 'Sent'}: {outbound}</span>
                      <span>{language === 'ar' ? 'وصل' : 'Delivered'}: {delivered}</span>
                      <span>{language === 'ar' ? 'قُرئ' : 'Read'}: {read}</span>
                      {failed > 0 && <span style={{ color: '#dc2626' }}>{language === 'ar' ? 'فشل' : 'Failed'}: {failed}</span>}
                      <span>{language === 'ar' ? 'ردود' : 'Replies'}: {replies}</span>
                      {isManagerOrOwner && (
                        <span>{language === 'ar' ? 'منسوبة' : 'Attributed'}: {campaign.attributed_conversations || 0}</span>
                      )}
                    </div>
                    <div style={{ marginTop: '7px', height: '8px', borderRadius: '99px', backgroundColor: '#e2e8f0', overflow: 'hidden', display: 'flex' }}>
                      <div style={{ width: `${Math.min(100, deliveryPct)}%`, backgroundColor: '#16a34a' }} />
                      <div style={{ width: `${Math.min(100, replyPct)}%`, backgroundColor: '#f97316' }} />
                      <div style={{ width: `${Math.min(100, readPct)}%`, backgroundColor: '#94a3b8' }} />
                    </div>
                    {isManagerOrOwner && riskFlags.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {riskFlags.map((flag) => (
                          <span
                            key={`${campaign.campaign_id}-${flag}`}
                            style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: '999px',
                              background: '#fff7ed',
                              color: '#9a3412',
                              border: '1px solid #fdba74'
                            }}
                          >
                            {flag}
                          </span>
                        ))}
                      </div>
                    )}
                    {isManagerOrOwner && riskFlags.length === 0 && outbound > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: '999px',
                            background: '#f0fdf4',
                            color: '#166534',
                            border: '1px solid #86efac'
                          }}
                        >
                          {language === 'ar' ? 'صحي' : 'Healthy'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {activeView === 'team' && isManagerOrOwner && (() => {
        const agents = (teamPerformance?.agents || []);
        const hasData = agents.some((a) => Number(a.messages_sent) > 0);

        const formatResponseTime = (minutes) => {
          if (!minutes && minutes !== 0) return language === 'ar' ? '—' : '—';
          const m = Number(minutes);
          if (m < 60) return `${m} ${language === 'ar' ? 'د' : 'min'}`;
          const h = Math.floor(m / 60);
          const rem = m % 60;
          return rem > 0
            ? `${h}${language === 'ar' ? 'س' : 'h'} ${rem}${language === 'ar' ? 'د' : 'm'}`
            : `${h} ${language === 'ar' ? 'ساعة' : 'h'}`;
        };

        const speedColor = (minutes) => {
          if (!minutes && minutes !== 0) return '#6b7280';
          const m = Number(minutes);
          if (m <= 5) return '#059669';
          if (m <= 30) return '#0ea5e9';
          if (m <= 60) return '#d97706';
          return '#dc2626';
        };

        const pct = (part, total) =>
          total > 0 ? Math.round((part / total) * 100) : 0;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Period filter pills */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                  {language === 'ar' ? 'الفترة:' : 'Period:'}
                </span>
                {[
                  { value: 'today',  arLabel: 'اليوم',        enLabel: 'Today' },
                  { value: 'week',   arLabel: 'آخر 7 أيام',   enLabel: 'Last 7 days' },
                  { value: 'month',  arLabel: 'آخر 30 يوماً', enLabel: 'Last 30 days' },
                  { value: 'all',    arLabel: 'الكل',          enLabel: 'All time' },
                  { value: 'custom', arLabel: 'فترة محددة',   enLabel: 'Custom range' }
                ].map(({ value, arLabel, enLabel }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPerfPeriod(value)}
                    style={{
                      padding: '5px 14px',
                      borderRadius: '20px',
                      border: `1.5px solid ${perfPeriod === value ? '#f97316' : '#e2e8f0'}`,
                      background: perfPeriod === value ? '#fff7ed' : '#f8fafc',
                      color: perfPeriod === value ? '#ea580c' : '#64748b',
                      fontWeight: perfPeriod === value ? 700 : 400,
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {language === 'ar' ? arLabel : enLabel}
                  </button>
                ))}
                {perfLoading && (
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {language === 'ar' ? 'جاري التحديث…' : 'Updating…'}
                  </span>
                )}
              </div>

              {/* Time mode toggle: Business hours vs Wall clock */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                  {language === 'ar' ? 'حساب الوقت:' : 'Time calc:'}
                </span>
                <button
                  type="button"
                  onClick={() => setPerfTimeMode('business')}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '20px',
                    border: `1.5px solid ${perfTimeMode === 'business' ? '#3b82f6' : '#e2e8f0'}`,
                    background: perfTimeMode === 'business' ? '#eff6ff' : '#f8fafc',
                    color: perfTimeMode === 'business' ? '#1d4ed8' : '#64748b',
                    fontWeight: perfTimeMode === 'business' ? 700 : 400,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <FaClock style={{ fontSize: '11px' }} />
                  {language === 'ar' ? 'ساعات العمل' : 'Business hours'}
                </button>
                <button
                  type="button"
                  onClick={() => setPerfTimeMode('wall_clock')}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '20px',
                    border: `1.5px solid ${perfTimeMode === 'wall_clock' ? '#3b82f6' : '#e2e8f0'}`,
                    background: perfTimeMode === 'wall_clock' ? '#eff6ff' : '#f8fafc',
                    color: perfTimeMode === 'wall_clock' ? '#1d4ed8' : '#64748b',
                    fontWeight: perfTimeMode === 'wall_clock' ? 700 : 400,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {language === 'ar' ? 'الوقت الفعلي' : 'Wall clock'}
                </button>
                {perfTimeMode === 'business' && teamPerformance?.business_settings && (
                  teamPerformance.business_settings.configured ? (
                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px' }}>
                      {teamPerformance.business_settings.work_start}–{teamPerformance.business_settings.work_end}
                      {' • '}
                      {teamPerformance.business_settings.working_days?.length === 7
                        ? (language === 'ar' ? 'كل الأيام' : 'All days')
                        : (language === 'ar'
                          ? `${teamPerformance.business_settings.working_days?.length || 0} أيام عمل`
                          : `${teamPerformance.business_settings.working_days?.length || 0} work days`)}
                    </span>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#d97706', background: '#fffbeb', padding: '3px 8px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                      {language === 'ar' 
                        ? 'لم يتم تحديد ساعات العمل — يُحسب كـ 24/7'
                        : 'No business hours set — calculating as 24/7'}
                    </span>
                  )
                )}
              </div>

              {/* Custom date range inputs — shown only when custom is selected */}
              {perfPeriod === 'custom' && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {language === 'ar' ? 'من:' : 'From:'}
                    </label>
                    <DualCalendarPicker
                      value={perfDateFrom}
                      onChange={(v) => setPerfDateFrom(v)}
                      max={perfDateTo || undefined}
                      language={language}
                      placeholder={language === 'ar' ? 'تاريخ البداية' : 'Start date'}
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {language === 'ar' ? 'إلى:' : 'To:'}
                    </label>
                    <DualCalendarPicker
                      value={perfDateTo}
                      onChange={(v) => setPerfDateTo(v)}
                      min={perfDateFrom || undefined}
                      language={language}
                      placeholder={language === 'ar' ? 'تاريخ النهاية' : 'End date'}
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                  {perfDateFrom && perfDateTo && perfDateFrom > perfDateTo && (
                    <span style={{ fontSize: '12px', color: '#dc2626' }}>
                      {language === 'ar' ? 'تاريخ البداية يجب أن يكون قبل النهاية' : 'Start date must be before end date'}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Summary strip */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
              gap: '10px'
            }}>
              {[
                {
                  label: language === 'ar' ? 'إجمالي الرسائل المُرسلة' : 'Total messages sent',
                  value: agents.reduce((s, a) => s + Number(a.messages_sent || 0), 0),
                  color: '#0f172a'
                },
                {
                  label: language === 'ar' ? 'محادثات مُعالَجة' : 'Conversations handled',
                  value: agents.reduce((s, a) => s + Number(a.conversations_handled || 0), 0),
                  color: '#0f172a'
                },
                {
                  label: language === 'ar' ? 'بانتظار رد الفريق' : 'Awaiting team reply',
                  value: agents.reduce((s, a) => s + Number(a.unanswered_count || 0), 0),
                  color: '#dc2626'
                }
              ].map((item) => (
                <div key={item.label} style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '14px 16px'
                }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>

            {/* Agent leaderboard table */}
            <div style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '14px 16px',
                borderBottom: '1px solid #f1f5f9',
                fontSize: '13px',
                fontWeight: 700,
                color: '#0f172a'
              }}>
                {language === 'ar' ? 'لوحة أداء الأعضاء' : 'Agent Performance Board'}
              </div>

              {!hasData ? (
                <div style={{ padding: '24px 16px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                  {language === 'ar' ? 'لا توجد بيانات كافية حتى الآن' : 'No data yet'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#64748b', textAlign: language === 'ar' ? 'right' : 'left' }}>
                        {[
                          language === 'ar' ? 'العضو' : 'Agent',
                          language === 'ar' ? 'رسائل' : 'Messages',
                          language === 'ar' ? 'محادثات' : 'Convos',
                          perfTimeMode === 'business'
                            ? (language === 'ar' ? 'متوسط وقت الرد (ساعات العمل)' : 'Avg Reply (Business)')
                            : (language === 'ar' ? 'متوسط وقت الرد (فعلي)' : 'Avg Reply (Wall)'),
                          language === 'ar' ? 'خلال 5 د' : '≤5 min',
                          language === 'ar' ? 'خلال ساعة' : '≤1 h',
                          language === 'ar' ? 'بانتظار رد' : 'Unanswered'
                        ].map((h, i) => (
                          <th key={i} style={{ padding: '8px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((agent, idx) => {
                        const msgsSent = Number(agent.messages_sent || 0);
                        const convos = Number(agent.conversations_handled || 0);
                        const avgReply = agent.avg_first_response_minutes != null ? Number(agent.avg_first_response_minutes) : null;
                        const total = Number(agent.first_response_total || 0);
                        const w5 = Number(agent.replied_within_5m || 0);
                        const w1h = Number(agent.replied_within_1h || 0);
                        const unanswered = Number(agent.unanswered_count || 0);
                        const isTop = idx === 0 && msgsSent > 0;

                        return (
                          <tr
                            key={agent.user_id}
                            style={{
                              borderTop: '1px solid #f1f5f9',
                              background: isTop ? '#fffbeb' : 'transparent'
                            }}
                          >
                            {/* Agent name */}
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {isTop && <span style={{ fontSize: '14px' }}>🥇</span>}
                                <span>{agent.agent_name || `#${agent.user_id}`}</span>
                              </div>
                            </td>
                            {/* Messages sent */}
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0f172a' }}>
                              {msgsSent}
                            </td>
                            {/* Convos handled */}
                            <td style={{ padding: '10px 12px', color: '#334155' }}>
                              {convos}
                            </td>
                            {/* Avg first response */}
                            <td
                              style={{ padding: '10px 12px', fontWeight: 700, color: speedColor(avgReply), cursor: 'help' }}
                              title={
                                agent.avg_first_response_business_minutes != null && agent.avg_first_response_wall_minutes != null
                                  ? `${language === 'ar' ? 'ساعات العمل' : 'Business'}: ${formatResponseTime(agent.avg_first_response_business_minutes)} | ${language === 'ar' ? 'فعلي' : 'Wall'}: ${formatResponseTime(agent.avg_first_response_wall_minutes)}`
                                  : undefined
                              }
                            >
                              {formatResponseTime(avgReply)}
                            </td>
                            {/* Speed buckets */}
                            <td style={{ padding: '10px 12px' }}>
                              {total > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                  <span style={{ fontWeight: 700, color: '#059669' }}>{pct(w5, total)}%</span>
                                  <span style={{ fontSize: '10px', color: '#94a3b8' }}>{w5}/{total}</span>
                                </div>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {total > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                  <span style={{ fontWeight: 700, color: '#0ea5e9' }}>{pct(w1h, total)}%</span>
                                  <span style={{ fontSize: '10px', color: '#94a3b8' }}>{w1h}/{total}</span>
                                </div>
                              ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            {/* Unanswered */}
                            <td style={{ padding: '10px 12px' }}>
                              {unanswered > 0 ? (
                                <span style={{
                                  display: 'inline-block',
                                  background: '#fef2f2',
                                  color: '#dc2626',
                                  border: '1px solid #fecaca',
                                  borderRadius: '999px',
                                  padding: '2px 8px',
                                  fontWeight: 700,
                                  fontSize: '11px'
                                }}>
                                  {unanswered}
                                </span>
                              ) : (
                                <span style={{ color: '#10b981', fontWeight: 700 }}>✓</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Speed distribution bar */}
            {hasData && (() => {
              const totalFirst = agents.reduce((s, a) => s + Number(a.first_response_total || 0), 0);
              const totalW5 = agents.reduce((s, a) => s + Number(a.replied_within_5m || 0), 0);
              const totalW1h = agents.reduce((s, a) => s + Number(a.replied_within_1h || 0), 0);
              const totalW24 = agents.reduce((s, a) => s + Number(a.replied_within_24h || 0), 0);
              if (totalFirst === 0) return null;
              const p5 = pct(totalW5, totalFirst);
              const p1h = pct(totalW1h - totalW5, totalFirst);
              const p24 = pct(totalW24 - totalW1h, totalFirst);
              const pLate = Math.max(0, 100 - pct(totalW24, totalFirst));
              return (
                <div style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '14px 16px'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
                    {language === 'ar' ? 'توزيع سرعة الرد الأول (للفريق كله)' : 'First Reply Speed Distribution (whole team)'}
                  </div>
                  <div style={{ display: 'flex', height: '12px', borderRadius: '999px', overflow: 'hidden', gap: '2px' }}>
                    {p5 > 0 && <div style={{ flex: p5, background: '#059669', borderRadius: '999px' }} title={`≤5 min: ${p5}%`} />}
                    {p1h > 0 && <div style={{ flex: p1h, background: '#0ea5e9', borderRadius: '999px' }} title={`5–60 min: ${p1h}%`} />}
                    {p24 > 0 && <div style={{ flex: p24, background: '#d97706', borderRadius: '999px' }} title={`1–24 h: ${p24}%`} />}
                    {pLate > 0 && <div style={{ flex: pLate, background: '#dc2626', borderRadius: '999px' }} title={`>24 h: ${pLate}%`} />}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap', fontSize: '11px' }}>
                    {[
                      { color: '#059669', label: language === 'ar' ? `خلال 5 د (${p5}%)` : `≤5 min (${p5}%)` },
                      { color: '#0ea5e9', label: language === 'ar' ? `5–60 د (${p1h}%)` : `5–60 min (${p1h}%)` },
                      { color: '#d97706', label: language === 'ar' ? `1–24 س (${p24}%)` : `1–24 h (${p24}%)` },
                      { color: '#dc2626', label: language === 'ar' ? `أكثر من 24 س (${pLate}%)` : `>24 h (${pLate}%)` }
                    ].map((item) => (
                      <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: item.color, display: 'inline-block' }} />
                        {item.label}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#94a3b8' }}>
                    {language === 'ar' ? `بناءً على ${totalFirst} محادثة تم قياس أول رد فيها` : `Based on ${totalFirst} first-reply measurements`}
                  </div>
                </div>
              );
            })()}

          </div>
        );
      })()}
    </div>
  );
};

export default WhatsAppInsights;
